import React from 'react';
import { Icon } from '@iq/react-components';

import { toNumDecimals as toNumberDecimals, getHexaDecimal, hexToRGB } from '../../../utils';
import { STATUSES, PAGE_TYPES, RISK_SEVERITY_ORDER } from '../../../constants';
import { units, format as unitFormat } from '../../../units';
/*
 * render/highlight/overlay/show reference: [
 *   0: None (default), 1: All, 2: Active, 3: Active else/or all
 * ]
 */
function shouldRenderComponent({
  isActive,
  hasActive,
  viewerClickedId,
  renderComponents,
  focusActiveComponent,
  isolate,
}) {
  if (focusActiveComponent && isActive && !viewerClickedId) {
    return true;
  }
  if (focusActiveComponent && hasActive && !isActive) {
    return false;
  }
  if (renderComponents === 1 && (!focusActiveComponent || !hasActive || viewerClickedId)) {
    return true;
  }
  if (renderComponents === 2 && isActive) {
    return true;
  }
  if (renderComponents === 3 && (isActive || (!isActive && !hasActive))) {
    return true;
  }
  return !isolate;
}

function shouldHighlightComponent({
  isActive,
  hasActive,
  viewerClickedId,
  highlightComponents,
  focusActiveComponent,
}) {
  if (focusActiveComponent && isActive && !viewerClickedId) {
    return false;
  }
  if (highlightComponents === 1 && (!focusActiveComponent || !hasActive || viewerClickedId)) {
    return true;
  }
  if (highlightComponents === 2 && isActive) {
    return true;
  }
  return highlightComponents === 3 && (isActive || (!isActive && !hasActive));
}

function getComponentOverlay(
  component,
  { isActive, hasActive, viewerClickedId, overlayComponents, focusActiveComponent },
  pageType
) {
  if ((!overlayComponents && !isActive) || pageType === PAGE_TYPES.none.toLowerCase()) {
    return null;
  }
  const ownStatus = component.ownStatus && component.ownStatus.status;
  const filteredStatus = component.filteredStatus && component.filteredStatus.status;

  const healthStatus = ownStatus >= 0 ? ownStatus : filteredStatus;

  const riskStatus =
    component.filteredStatus?.eventRiskStatus?.toSorted(
      (a, b) => RISK_SEVERITY_ORDER.indexOf(a.status) - RISK_SEVERITY_ORDER.indexOf(b.status)
    )[0] || {};
  const getOverlayColor = () => {
    const colorObject = {
      risk: riskStatus && STATUSES[riskStatus.status]?.colorHex,
      stateset:
        component.state?.color &&
        (component.state?.quality === 1 || component.state?.area) &&
        getHexaDecimal(component.state.color),
      health: STATUSES[healthStatus]?.colorHex,
    };
    return colorObject[pageType] || null;
  };

  const color = getOverlayColor();
  if (color || color === 0) {
    if (overlayComponents && focusActiveComponent && isActive && !viewerClickedId) {
      return color;
    }
    if (overlayComponents === 1 && (!focusActiveComponent || !hasActive || viewerClickedId)) {
      return color;
    }
    if (overlayComponents === 2 && isActive) {
      return color;
    }
    if (overlayComponents === 3 && (isActive || !hasActive)) {
      return color;
    }
  }
  return null;
}

function shouldShowPin(
  component,
  pushPin,
  { isActive, hasActive, viewerClickedId, showPushPins, displayErrorPins, focusActiveComponent }
) {
  const hasError = component.ownStatus && component.ownStatus.status > 0;
  if (displayErrorPins && hasError) {
    return true;
  }
  if (!pushPin) {
    return false;
  }
  if (focusActiveComponent && isActive && !viewerClickedId) {
    return true;
  }
  if (showPushPins === 1 && (!focusActiveComponent || !hasActive || viewerClickedId)) {
    return true;
  }
  if (showPushPins === 2 && isActive) {
    return true;
  }
  return showPushPins === 3 && (isActive || (!isActive && !hasActive));
}

function getPinStyle(component, pageType) {
  const riskStatus =
    component.filteredStatus?.eventRiskStatus?.toSorted(
      (a, b) => RISK_SEVERITY_ORDER.indexOf(a.status) - RISK_SEVERITY_ORDER.indexOf(b.status)
    )[0] || {};

  const healthStatus = component.filteredStatus?.status > 0 && component.filteredStatus.status;

  const getPinColor = () => {
    const colorObject = {
      none: STATUSES.default.color,
      risk: riskStatus && STATUSES[riskStatus.status]?.color,
      stateset:
        component.state?.color &&
        (component.state?.quality === 1 || component.state?.area) &&
        hexToRGB(component.state.color),
      health: STATUSES[healthStatus]?.color,
    };
    return colorObject[pageType] || colorObject.none;
  };

  const color = getPinColor();

  return {
    backgroundColor: `rgba(${color}, 0.4)`,
    border: `1px solid rgb(${color})`,
  };
}

function getPushpinTooltipContent(comp, pushPin, pageType, timezone, height) {
  const content = [];

  const riskStatus =
    comp.filteredStatus?.eventRiskStatus?.toSorted(
      (a, b) => RISK_SEVERITY_ORDER.indexOf(a.status) - RISK_SEVERITY_ORDER.indexOf(b.status)
    )[0] || {};
  const healthStatus = comp.filteredStatus;

  const getPinContent = () => {
    switch (pageType) {
      case 'risk': {
        return {
          status: riskStatus?.status,
          statusText: riskStatus?.mitigation && riskStatus.eventText,
          riskMitigation: riskStatus?.mitigation,
          risks: () => {
            const riskValue = Object.entries(riskStatus)
              .filter(([k]) => k.includes('fr') || k.includes('mttr'))
              .map(([k, v]) => {
                let time = '';
                const value = v.toFixed(2);
                const property = k.toLowerCase();
                if (property.includes('day')) {
                  time = '2 days';
                } else if (property.includes('week')) {
                  time = '1 week';
                } else if (property.includes('month')) {
                  time = '1 month';
                }
                return {
                  time: value,
                  timeUnit: k.includes('fr') ? 'h' : '',
                  timespan: time,
                  type: k.includes('fr') ? 'fr' : 'mttr',
                };
              });
            const AvailabilityRisk = riskValue.filter((x) => x.type === 'fr');
            const ShutdownRisk = riskValue.filter((x) => x.type === 'mttr');
            const risk = {
              ['Availability Risk']: AvailabilityRisk,
              ['Shutdown Risk']: ShutdownRisk,
            };
            return risk;
          },
        };
      }

      case 'health': {
        return {
          status: healthStatus?.status || 0,
          statusText: healthStatus?.statusText,
          pushpins: pushPin.variables,
        };
      }

      default: {
        return {
          pushpins: pushPin.variables,
        };
      }
    }
  };

  const { status, statusText, pushpins, risks, riskMitigation } = getPinContent();

  const { color, icon } = STATUSES[status] || {};
  const [r, g, b] = color || [0, 0, 0];
  if (pushpins) {
    for (const {
      variableDefaultUnit,
      name,
      value: value_,
      quality,
      unit,
      maxDecimals = 3,
    } of pushpins) {
      let unitLabel;
      let value = '-';
      const displayUnitId = unit === 'variable-default' ? variableDefaultUnit : unit;
      const displayUnitDefinition = units.find(({ id }) => id === displayUnitId);
      const isTextUnit = ['Boolean', 'Date'].includes(displayUnitDefinition?.category);
      if (isTextUnit && value_ != null && value_ !== '') {
        const formatted = unitFormat(displayUnitId, value_, maxDecimals, null, timezone);
        value = formatted.value;
        unitLabel = formatted.unit;
      } else if (!Number.isNaN(Number.parseInt(value_, 10)) && !isTextUnit) {
        value = toNumberDecimals(value_, maxDecimals, maxDecimals > 0);
        const fallbackUnitLabel = unit === 'variable-default' ? variableDefaultUnit : undefined;
        unitLabel =
          typeof displayUnitDefinition?.unit === 'function'
            ? displayUnitDefinition.unit(value, maxDecimals)
            : (displayUnitDefinition?.unit ?? fallbackUnitLabel);
      }

      content.push({
        title: name,
        value,
        unit: unitLabel,
        quality,
      });
    }
  }
  return (
    <div className="pushpin-tooltip--wrapper">
      <div className="pushpin-tooltip--header">
        <div className="pushpin-tooltip--description">
          <div title={comp.name}>{comp.name}</div>
          <div title={comp.itemDesignation}>{comp.itemDesignation}</div>
        </div>
        <div className="pushpin-tooltip--icon">
          <Icon icon={comp.icon} />
        </div>
      </div>
      {status && status !== 0 && (
        <div
          className="pushpin-tooltip--status"
          style={{ backgroundColor: `rgba(${color}, 0.85)` }}
        >
          <div className="pushpin-tooltip--status--icon">
            <Icon
              icon={icon}
              style={{ color: `rgba(${r}, ${g}, ${b}, ${color ? 1 : 0})` }}
            />
          </div>
          <div className="pushpin-tooltip--status--text">{statusText}</div>
        </div>
      )}
      {content.length > 0 && (
        <div className="pushpin-tooltip--values">
          {content.map(({ title, value, unit, quality }, index) => (
            <div
              key={value + index}
              className={`value-wrapper ${quality === 1 ? '' : 'invalid-data'}`}
            >
              <div
                className="title"
                title={title}
              >
                {title}
                {quality === 1 ? null : (
                  <Icon
                    icon="he-help"
                    size="s"
                    color="#FF7300"
                  />
                )}
              </div>
              <div className="value">
                {value}
                <span className="unit">{unit}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {riskMitigation && (
        <div className="pushpin-tooltip--values">
          <div className="pushpin-tooltip--values">
            {Object.keys(risks).map((key, index) => {
              const riskTime = risks[key];
              return (
                <div
                  key={index}
                  className="value-wrapper"
                >
                  <div className="suggestion-availability">{key}</div>
                  {riskTime.map((value, index) => (
                    <div
                      key={index + 1}
                      className="suggestion-availability"
                    >{`${value.time} ${value.timeUnit} in ${value.timespan}`}</div>
                  ))}
                </div>
              );
            })}
          </div>
          <div className="suggestion-description">
            <div className="value-wrapper">
              <br />
              1st mitigation recommendation
            </div>
            <div
              style={{ maxHeight: height > 250 ? height - 240 : '100%' }} // Min height of the viewer panel in 240px
              className="description-text"
            >
              {riskMitigation}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getComponentsTooltipContent(tooltip, timezone) {
  const content = [
    {
      title: tooltip?.displayName,
      value: tooltip?.displayValue,
      quality: 1,
      message: tooltip?.freeText,
      stateText: tooltip?.stateText,
      stateDescription: tooltip?.stateDescription,
      stateQuality: tooltip?.quality,
      multipleActiveStates: tooltip?.multipleActiveStates,
    },
  ];

  if (tooltip?.variables) {
    for (const {
      name,
      value,
      quality,
      unit,
      variableDefaultUnit,
      maxDecimals = 3,
    } of tooltip.variables) {
      let valueText = '-';

      const displayUnitId = unit === 'variable-default' ? variableDefaultUnit : unit;
      const displayUnitDefinition = units.find(({ id }) => id === displayUnitId);
      const isTextUnit = ['Boolean', 'Date'].includes(displayUnitDefinition?.category);
      if (isTextUnit && value != null && value !== '') {
        const formatted = unitFormat(displayUnitId, value, maxDecimals, null, timezone);
        valueText = formatted.unit?.trim()
          ? `${formatted.value} ${formatted.unit}`
          : formatted.value;
      } else if (!Number.isNaN(Number.parseInt(value, 10)) && !isTextUnit) {
        const formattedValue = toNumberDecimals(value, maxDecimals, maxDecimals > 0);
        const fallbackUnitLabel = unit === 'variable-default' ? variableDefaultUnit : undefined;
        const unitLabel =
          typeof displayUnitDefinition?.unit === 'function'
            ? displayUnitDefinition.unit(value, maxDecimals)
            : (displayUnitDefinition?.unit ?? fallbackUnitLabel);
        valueText = unitLabel ? `${formattedValue} ${unitLabel}` : formattedValue;
      }

      content.push({
        title: name,
        value: valueText,
        quality,
      });
    }
  }

  return content.map(
    (
      {
        title,
        value,
        quality,
        message,
        stateText,
        stateDescription,
        stateQuality,
        multipleActiveStates,
      },
      index
    ) => (
      <div key={title + index}>
        <div>
          <div className={`value-wrapper ${quality === 1 ? '' : 'invalid-data'}`}>
            <div
              className="title"
              title={title}
            >
              {title}
              {quality === 1 ? null : (
                <Icon
                  icon="he-help"
                  size="s"
                  color="#FF7300"
                />
              )}
            </div>
            <div className="value">{value}</div>
          </div>
          {message ? <div className="message">{message}</div> : null}
        </div>
        {stateText ? (
          <div>
            <div className="value">
              {multipleActiveStates && stateQuality !== 1 ? (
                <Icon
                  icon="he-help"
                  size="s"
                  color="#FF7300"
                />
              ) : null}
              {stateText}
              {!multipleActiveStates && stateQuality !== 1 ? (
                <Icon
                  icon="he-help"
                  size="s"
                  color="#FF7300"
                />
              ) : null}
            </div>
            {stateDescription && stateText !== stateDescription ? (
              <div className="value">{stateDescription}</div>
            ) : null}
          </div>
        ) : null}
      </div>
    )
  );
}

export {
  shouldRenderComponent,
  shouldHighlightComponent,
  getComponentOverlay,
  shouldShowPin,
  getPinStyle,
  getPushpinTooltipContent,
  getComponentsTooltipContent,
};
